import { Platform } from "react-native";
import axios from "axios";
import axiosRetry from "axios-retry";
import * as Sentry from "@sentry/react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import RNFetchBlob from "react-native-blob-util";
import { codeBundleId } from "@lib/sentry";
import { baseURL, appVersion, appVersionCode } from "../config";
import {
  onFulfilledRequestInterceptor,
  onFulfilledResponseInterceptor,
  onRejectedResponseInterceptor,
} from "./api-utils";
import getTokenZ from "@utils/getTokenZ";

export const getToken = async () => {
  try {
    return {
      token: await AsyncStorage.getItem("user_token"),
    };
  } catch (e) {
    Sentry.captureException(e);
  }
};

// The server treats a request without `x-platform-os: WEB` as an Android build and
// applies the min-app-version force-update floor; the rn-web build reports a
// fake Android version code, so it must identify itself. Native builds send nothing.
const webPlatformHeaders =
  Platform.OS === "web" ? { "x-platform-os": "WEB" } : {};

export const api = axios.create({
  baseURL,
  timeout: 10000,
  headers: {
    "x-app-name": "CL",
    "x-app-version": appVersion,
    "x-app-version-cp": codeBundleId,
    "x-app-version-code": appVersionCode,
    "use-applinks-cl": "true",
    ...webPlatformHeaders,
  },
});

// eslint-disable-next-line import/no-named-as-default-member
axiosRetry(api, { retryDelay: axiosRetry.exponentialDelay, retries: 3 });

api.interceptors.request.use(onFulfilledRequestInterceptor);

api.interceptors.response.use(
  onFulfilledResponseInterceptor,
  onRejectedResponseInterceptor,
);

export const getError = () => api.get("/leader/get-error");

// api calls start here

// category related apis

export const getAllCategories = () => api.get("/products/all-category-listing");

export const getAllPrimaryCategoriesWithProductTypes = () =>
  api.get("/products/get-category-list");

// product related apis

// export const getProductByCategory = ({ category_url, offset, limit }) =>
//   api.get(
//     `/leader/tl-products-new/${category_url}?offset=${offset}&limit=${limit}`,
//   );

export const unSuspendMe = (data) => api.post("/leader/unsuspend-me", data);

export const getProductDetails = (slug) => api.get(`/products/${slug}`);

export const getProductFullDetails = (slug) =>
  api.get(`/products/single-product-detail/${slug}`);

export const getProductsBySearch = (searchTerm) =>
  api.post("/leader/search-product-new", { product: searchTerm });

export const getLeaderCustomSkuList = (data = {}) =>
  api.post("/leader/get-leader-custom-sku-list", data);

export const cancelLeaderCustomSkuOrder = (data = {}) =>
  api.post("/leader/cancel-leader-custom-sku-order", data);

export const getLeaderCustomSkuOrders = (data = {}) =>
  api.post("/leader/get-leader-custom-sku-orders", data);

export const getLeaderPaymentDetails = (data = {}) =>
  api.post("/leader/get-leader-payment-details", data);

export const createLeaderPayment = (data = {}) =>
  api.post("/leader/create-custom-order", data);

export const completeLeaderPayment = (data = {}) =>
  api.post("/leader/complete-custom-order", data);

export const placeNewOrder = (data = {}) =>
  api.post("/orders/create-order", data);

export const completeOrder = (data = {}) =>
  api.post("/orders/complete-order", data);

export const getAllOrders = ({
  offset = 0,
  limit = 10,
  query = "",
  userId = "",
  date,
  type = "NOT_ASSIGNED",
  vacationMode = false,
}) =>
  api.get(
    `/leader/orders-list?offset=${offset}&limit=${limit}&searchTerm=${query}&userId=${userId}&date=${date}&vacationMode=${vacationMode}&type=${type}`,
  );

export const getCustomerOrders = ({
  url,
  user_id = "",
  offset,
  limit,
  query = "",
}) =>
  api.get(
    `/leader/order-copy/${url}/${user_id}?offset=${offset}&limit=${limit}&searchTerm=${query}`,
  );

// export const changeNumberRequest = (data) =>
//   api.post("/leader/change-number-request", data);

// export const verifyChangeNumberRequest = (data) =>
//   api.post("/leader/verify-change-number-request", data);

export const getOrderDetailById = ({ orderId, isVacationOrder }) =>
  api.get(
    `/leader/order-details/${orderId}?isVacationOrder=${isVacationOrder}`,
  );

export const getOrderDetailByIdV2 = ({
  orderId,
  isVacationOrder = false,
  source = "FORWARD",
}) =>
  api.get(
    `/leader/order-details-v2/${orderId}?isVacationOrder=${isVacationOrder}&source=${source}`,
  );

export const getSingleOrderDetails = (orderId = "") =>
  api.get(`users/order-details/${orderId}`);

export const orderCancellationReasons = () =>
  api.get("/leader/cancellation-reasons");

export const cancelOrder = (data = {}) => {
  return api.post("/leader/cancel", data);
};

export const getRatingPlayStore = () => api.get("/leader/rating-check"); // TODO - remove this api, handle this using /me

export const giveRatingPlayStore = () =>
  api.post("/leader/rating-update", { rating_done: true });

export const shouldShowInAppRating = () =>
  api.get("/leader/show-inapp-app-rating");

export const shouldInAppUpdate = (currentVersion = "") =>
  api.get(`/leader/show-inapp-update?currentVersion=${currentVersion}`);

// user or team leader related api calls here

export const signUp = (data = {}) => {
  return api.post("/leader/sign-up-team-leader", data);
};

export const sendOtpToUser = (
  phone_number = "",
  isDebug = false,
  checkBox = false,
) => {
  const data = {
    phone_number,
    isDebug,
    trackingInfo: {},
    source: "app",
  };
  if (checkBox) {
    return api.post("/leader/get-otp-db", data);
  }
  return api.post("/leader/get-otp-v2", data);
};

export const registerNewNumber = (
  phone_number = "",
  name = "",
  isDebug = true,
  code = "",
  gender = "",
) => {
  const data = {
    phone_number,
    name,
    isDebug,
    code,
    gender,
  };
  return api.post("/leader/update-leader-name", data);
};

export const checkClCode = (code = "") => {
  const data = {
    code,
  };
  return api.post("/leader/verify-signup-code", data);
};

export const verifyOtpForLogin = async (data = {}, checkbox = false) => {
  if (checkbox) {
    return api.post("/leader/verify-db-otp", { ...data, source: "app" });
  }
  return api.post("/leader/verify-tl-user", { ...data, source: "app" });
};

export const retryDevice = (data = {}) =>
  api.post("/leader/device-verification/retry", data);
export const loginRefresh = ({
  ivrSessionId = "",
  phone_number = "",
  newTcUser = "",
}) =>
  api.get(
    `/leader/device-verification/ivr-session-status?ivrSessionId=${ivrSessionId}&phone_number=${phone_number}&newTcUser=${newTcUser}`,
  );

export const getUserDetails = (cache) => {
  const finalUrl = cache ? "/leader/me?from=cache" : "/leader/me";
  return api.get(finalUrl);
};

export const getTotalPayout = () => api.post("/leader/total-sale-payout");

export const getDailySalesStats = () => api.post("/leader/team-leader-stats");

export const getTotalDailyPayout = () => api.post("/leader/team-leader-payout");

export const addNewProductRequest = (requestProduct) =>
  api.post("leader/add-request-product", { requestProduct });

export const getAllProductRequests = () => api.get("/leader/request-product");

export const addLeaderContacts = (contacts = {}) =>
  api.post("/leader/add-tl-contact", { contacts });

export const isLeaderAlreadyHaveContacts = () =>
  api.get("/leader/is-user-have-contacts");

export const leaderReferMessage = () => api.get("/leader/refer-message");

export const searchProducts = (searchTerm = "") =>
  api.get(`/leader/search/products?searchTerm=${searchTerm}`);

// cart related api calls here
export const setCartItem = (data = {}) =>
  api.post("/leader/set-cart-item", data);

export const addToCart = (data = {}) => api.post("/leader/add-to-cart", data);
export const removeFromCart = (data = {}) =>
  api.post("/leader/remove-from-cart", data);

export const viewCart = ({ userId }) =>
  api.post("/leader/view-cart-new", { userId });

export const placeOrder = (data = {}) =>
  api.post("/leader/create-order-2", data);

export const completePaymentOld = (data = {}) =>
  api.post("/leader/complete-order-2", data);

export const completePayment = (data = {}) =>
  api.post("/leader/complete-order-3", data);

export const whatsAppOpt = (whatsapp_opt = false) => {
  return api.post("/leader/opt-whatsapp", { whatsapp_opt });
};

export const applyCouponCode = (data = {}) =>
  api.post("/leader/apply-coupon", data);

export const removeCouponCode = (data = {}) =>
  api.post("/leader/remove-coupon", data);

export const createTlUser = (data = {}) =>
  api.post("leader/create-tl-user", data.userCreds);

export const editTlUser = ({ user_id = "", userData }) =>
  api.post(`leader/edit-tl-contact/${user_id}`, userData);

export const deleteClUser = (user_id = "", selected) =>
  api.delete(`leader/delete-cl-user/${user_id}?reason=${selected}`);

export const getAllTemLeaders = () => api.get("leader/view-team-leader");

// export const getAllTeamLeaderUsers = () => api.get(`leader/users`);
// old_one_gagan_commented
// export const getAllTeamLeaderUsers = ({ limit, offset, query = "" }) =>
//   api.get(
//     `leader/users-copy?limit=${limit}&offset=${offset}&searchTerm=${query}`,
//   );
export const getAllTeamLeaderUsers = () => api.get("leader/all-customers");

export const getTlUser = (user_id = "") =>
  api.get(`leader/users-new/${user_id}`);

export const getAllLeaderCustomers = ({
  limit,
  offset,
  query = "",
  cohortId,
}) =>
  api.get(
    `leader/customers?cohort_id=${cohortId}&limit=${limit}&offset=${offset}&search_term=${query}`,
  );

export const deleteTeamLeaderUser = ({ userId }) =>
  api.delete(`leader/users/${userId}`);

export const getBannerImages = () => api.get("app/get-all-banners");

// PDF Sharing related
export const getPDFOfProducts = () => api.get("products/all-product-pdf");

export const getPDFOfProductByCategory = (url) =>
  api.get(`products/all-cat-product-pdf/${url}`);

// VERSION
export const getAppVersion = () => api.get("leader/version");

export const getEmergencyMessage = () => api.get("leader/emergency-message");

// Necessary contacts
export const getSupportContacts = () => api.get("leader/support-phonenumber");

export const getSupportAndOnboardingNumbers = () =>
  api.get("leader/onboarding-support-number");

export const getWAContacts = () => api.get("leader/wa-group-phonenumbers");

export const getPincodeByLatLong = (lat = "", long = "") =>
  api.get(`leader/reverse-geocode?lat=${lat}&lng=${long}`);

export const increaseOnboardingStatus = () =>
  api.post("leader/onboarded-step-one");

export const generatCXLink = (phone_number) =>
  api.get(`leader/leader-app-share-link?phoneNumber=${phone_number}`);

// Leader Bank account details
export const manageBankDetails = (details) =>
  api.post("leader/edit-tl-bank-account", details);
// support apis

export const getSupportItemOrListOld = ({ id, type }) => {
  return api.get(`support/cl/config?id=${id}&type=${type}`);
};

export const getSupportItemOrList = ({ id, type, item_id }) => {
  return api.get(
    `support/v2/cl/config?id=${id}&type=${type}&item_id=${item_id}`,
  );
};

export const sendReply = (data) => api.post("support/cl/send-reply", data);
export const addTicketRating = (data) =>
  api.post("support/cl/add-rating", data);

export const cancelTicket = (data) =>
  api.post("support/cl/cancel-ticket", data);

export const reopenTicket = (data) =>
  api.post("support/cl/reopen-ticket", data);

export const createNewSupportTicket = (data) =>
  api.post("support/cl/new", data);

export const fetchOpenTickets = () => {
  return api.get("support/cl/get");
};
export const fetchOpenTicketsNew = ({
  limit = 10,
  offset = 0,
  filter = "all",
}) => {
  return api.get(
    `support/cl/get-new?filter=${filter}&limit=${limit}&offset=${offset}`,
  );
};

export const fetchTicketDetails = (id) => {
  return api.get(`support/cl/get-ticket?id=${id}`);
  // return new Promise(resolve => resolve({ data: openTickets[0] }));
};

export const getCouponsForUser = (userId) =>
  api.get(`leader/get-coupons?user_id=${userId}`);

export const getLeaderDetails = (id) =>
  api.get(`leader/get-leader-details?id=${id}`);

export const updateAlternatePhone = (alternate_phone) =>
  api.post("leader/update-alternate-number", { alternate_phone });

export const getAllGroupBuys = () => api.get("/leader/all-group-buys");

export const getAllGroupBuysWithOrders = () =>
  api.get("/leader/all-group-buys-with-orders");

export const getGroupBuyById = (id) => api.get(`/leader/group-buy/${id}`);

export const getGroupBuyCustomers = ({ id, limit, offset, searchTerm }) => {
  return api.get(
    `/leader/group-buy/${id}/customers?limit=${limit}&offset=${offset}&searchTerm=${searchTerm}`,
  );
};

export const groupBuy = ({ orders = [], gbId = "" }) => {
  return api.post("/leader/group-buy", { gbId, orders });
};

export const getDeliveriesList = (limit = 10, offset = 0) => {
  return api.get(`/leader/deliveries-list?limit=${limit}&offset=${offset}`);
};

export const markOrderAsNotDelivered = (payload) => {
  return api.post("/leader/mark-cx-order-as-not-delivered", payload);
};

export const getPDG = () => {
  return api.get("/leader/get-pdg");
};

export const getLeaderBcard = () => {
  return api.get("/leader/get-bcard");
};

export const getDeliveryListCount = () => {
  return api.get("/leader/deliveries-list-count");
};
export const getReferralBanners = () => {
  return api.get("/leader/get-cl-referral-banners");
};

export const getReferedCustomers = () => {
  return api.get("/leader/get-cl-referred-customers");
};

export const getReferredCustomersV2 = () => {
  return api.get("/leader/get-cl-referred-customers-v2");
};

export const getAllOosSkus = () => api.get("/leader/get-oos-sku");

export const notifyForOOS = (payload) =>
  api.post("/leader/notify-for-oos", payload);

export const getAllBanners = () => api.get("/leader/banners");

// ccfp apis

export const getCCFPSummary = () => api.get("/leader/ccfp/summary");
export const getCCFPLandingInfo = () => api.get("/leader/ccfp/landing-info");
export const getCCFPPassbook = ({ offset = 0, limit = 100 }) =>
  api.get(`/leader/ccfp/get-all-transactions?limit=${limit}&offset=${offset}`);
export const registerCCFP = () => api.post("/leader/ccfp/register");
export const withdrawCFFPMoney = (payload) =>
  api.post("/leader/ccfp/withdraw", payload);

// wallet apis

export const getWalletOtp = (payload) =>
  api.post("/leader/wallet/get-otp", { ...payload });

export const validateUpi = (payload) =>
  api.post("/leader/wallet/validate-upi-v2", { ...payload });

export const deleteFundAccount = (payload) =>
  api.post("/leader/wallet/delete-fund-account-v2", payload); // new

export const createFundAccountBank = (payload) =>
  api.post("/leader/wallet/fund-account-v2", {
    ...payload,
  }); // new

export const withdrawMoney = (payload) =>
  api.post("/leader/wallet/withdraw", payload);

export const editBlackListedFundAccount = (payload) =>
  api.post("/leader/wallet/edit-fund-account", payload);

export const editUserFundAccount = (payload) =>
  api.post("/leader/wallet/edit-user-fund-account", payload);

export const createFundAccountBankOld = (payload) =>
  api.post("/leader/wallet/fund-account", {
    ...payload,
  });

export const deleteFundAccountOld = (payload) =>
  api.post("/leader/wallet/delete-fund-account", payload);

export const validateUpiOld = (payload) =>
  api.post("/leader/wallet/validate-upi", payload);

export const gullakCms = () => api.get("/leader/wallet/cms");

export const validateAmount = (payload, mode) =>
  api.post("/leader/wallet/validate-amount", { amount: payload, mode }); // validate

export const setPrimaryFundAccount = (payload) =>
  api.post("/leader/wallet/set-default", payload);

// get

export const getIfscDetails = (ifsc) =>
  api.get(`/leader/wallet/ifsc?ifsc=${ifsc}`); // validate
export const getFundAccounts = () => api.get("/leader/wallet/fund-accounts");
export const getAllWalletMetaData = () => api.get("/leader/wallet-metadata");

export const getUpiMetadata = () => api.get("/leader/wallet/wallet-meta");

export const getWalletSummary = ({ from, to }) =>
  api.get(`/leader/wallet-summary?fromDate=${from}&toDate=${to}`);
export const getWalletIdfaStatus = () => api.get("/leader/wallet-idfa-status");

export const setAppConfigsApi = (
  versionName,
  versionNumber,
  fcm_token,
  notificationPermissionStatus,
) =>
  api.post("/leader/set-one-signal-user-id", {
    versionName,
    versionNumber,
    fcm_token,
    notificationPermissionStatus,
  });

export const getWalletTransactionSummary = ({
  type = "",
  from,
  to,
  offset = 0,
  limit = 100,
}) =>
  api.get(
    `leader/wallet-transaction-by-type?fromDate=${from}&toDate=${to}&type=${type}&limit=${limit}&offset=${offset}`,
  );

export const getPassbookTransactions = ({
  from,
  to,
  offset = 0,
  limit = 100,
  wt_ids,
}) => {
  // if more then one transactions send it as comma separated wt_ids
  if (wt_ids) {
    return api.get(
      `/leader/all-wallet-transactions?fromDate=${from}&toDate=${to}&limit=${limit}&offset=${offset}&wt_ids=${wt_ids}`,
    );
  }
  return api.get(
    `/leader/all-wallet-transactions?fromDate=${from}&toDate=${to}&limit=${limit}&offset=${offset}`,
  );
};
export const resetClCache = () => api.get("/leader/reset-cl-cache");

export const setBotViewedItems = (payload) =>
  api.post("/botcms/viewed-items", payload);

export const getHomePage = ({ limit = 20 }) =>
  api.get(`/cms/home-page?limit=${limit}`);

export const getCLTrainingPage = () => api.get("/cms/cl-training-page");
export const oldGetCMSEntityById = ({
  id,
  limit = 10,
  offset = 0,
  root = false,
  // user_id,
  complete = false,
}) =>
  api.get(
    `/cms?id=${id}&limit=${limit}&offset=${offset}&app=cl&isRoot=${root}&bundled=yes&complete=${complete}`,
  );

// Group Buy
export const getAllGroupBuysv2 = () => api.get("/leader/all-group-buys-v2");

export const getAllOffersOnWa = () => api.get("/leader/wa-statuses");

// rating apis

export const getClReviewOptions = () =>
  api.get("/leader/get-cl-review-options");

export const addRatingReview = (payload) =>
  api.post("/leader/add-rating-review-cl", payload);

export const addRatingReviewForOrder = (payload) =>
  api.post("/leader/add-rating-review-cl-for-order", payload);

export const addRatingReviewForOrderNew = (payload) =>
  api.post("/leader/cl-delivery-rating", payload);

// https://maaptol.in/api/leader/check-cl-order-rating

export const checkClRating = () => api.get("/leader/check-cl-order-rating-new");

export const getLocationByZipCode = (pincode) => {
  return api.get(`leader/get-city-from-pincode/${pincode}`);
};

export const placesAutocomplete = (input) => {
  return api.get(`leader/google-places/places-autocomplete?input=${input}`);
};

export const getLatLngByPlaceId = (place_id) => {
  return api.get(
    `/leader/google-places/address-from-placeid?placeid=${place_id}`,
  );
};

export const getAddressByLatLng = (lat, lng) => {
  return api.get(
    `/leader/google-places/address-from-lat-lng?lat=${lat}&lng=${lng}`,
  );
};

export const getLatLngByAddress = (add) => {
  return api.get(`/leader/google-places/lat-lng-from-address?address=${add}`);
};

export const setTrackingInfo = (trackingInfo) => {
  return api.post("/leader/tracking-info", { trackingInfo });
};

export const getSuggestedProducts = (slug) =>
  api.get(`/products/popular-product/${slug}`);

export const getAppSettings = () => api.get("/leader/app-settings");

export const submitPincode = (data) => api.post("/leader/set-pincode", data);
// Sign up as a new CL who is already a customer - with order / without order

export const getMyLeader = () => api.get("/leader/find-my-leader");

export const joinCLProgram = () =>
  api.post("/leader/remove-already-a-cx-wo-order");

// undiscard the leader..

export const undiscardMe = (data) => api.post("/leader/undiscard-me", data);

// profile related apis

export const getDropDownOptions = () =>
  api.get("/leader/cl-profile-dropdown-option-1");

export const getDropDownOptions_old = () =>
  api.get("/leader/cl-profile-dropdown-option");

export const updateClAddress = (data) =>
  api.post("/leader/update-cl-profile-address-other-details", data);

export const updateBasicInfo = (data) =>
  api.post("/leader/update-cl-profile-other-details", data);

// axios v1 fails to recognize React Native's FormData polyfill (its
// `instanceof FormData` check misfires on Hermes/Metro, and the polyfill
// doesn't tag itself via Symbol.toStringTag), so axios's default transform
// JSON-stringifies the FormData into {"_parts":[...]}. Even when detection
// passes, `resolveConfig` only strips Content-Type for browser envs — not
// React Native — so the boundary is never injected.
// RN's native fetch handles its own polyfill correctly, so all multipart
// uploads go through this helper instead of the axios `api` instance.
// Returns an axios-shaped { data, status } so callers don't need to change.
async function uploadMultipart(path, data) {
  const token = await getTokenZ();
  const url = `${baseURL}${path.replace(/^\/+/, "")}`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: token,
      "x-app-name": "CL",
      "x-app-version": appVersion,
      "x-app-version-code": appVersionCode,
      ...webPlatformHeaders,
      // Intentionally NOT setting Content-Type — RN's fetch will set
      // "multipart/form-data; boundary=..." with the correct boundary.
    },
    body: data,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${path} ${res.status}: ${text}`);
  }
  return { data: await res.json().catch(() => ({})), status: res.status };
}

export const uploadImage = (data) =>
  uploadMultipart("media/upload-image", data);

export const uploadAttachments = (data) =>
  uploadMultipart("media/upload-attachments", data);

export const uploadCloudfront = (data) =>
  api.post("media/upload-cloudfront", data);

export const deleteImage = (filename) =>
  api.post("media/delete-image", { filename });

export const deleteCloudfront = (filename) =>
  api.post("media/delete-cloudfront", { filename });

export const uploadPrivateImage = (data) =>
  uploadMultipart("leader/upload-private-image", data);

export const deletePrivateImage = (filename) =>
  api.post("/leader/delete-private-image", { filename });

// Crate handover videos — the partner records per-crate videos that gate the
// MH DB app's delivery marking. Backed by /leader/handover-video/* (commons
// videoCaptures module in citymall-services).
const HANDOVER_VIDEO = "/leader/handover-video";

export const getHandoverVideoConfig = () => api.get(`${HANDOVER_VIDEO}/config`);

export const getHandoverVideoContext = (date) =>
  api.get(`${HANDOVER_VIDEO}/context${date ? `?date=${date}` : ""}`);

// Crate contents (name/image/qty) for the View Details modal.
export const getHandoverCrateItems = (crateId, type) =>
  api.get(
    `${HANDOVER_VIDEO}/crate-items?crate_id=${encodeURIComponent(
      crateId,
    )}&type=${type}`,
  );

// { crate_id, type: 'CRATE_HANDOVER' | 'RVP_CRATE_PICKUP', lat?, lng? }
// → { id, upload_url, content_type, limits }
export const initHandoverVideo = (data) =>
  api.post(`${HANDOVER_VIDEO}/init`, data);

export const completeHandoverVideo = (id, durationS) =>
  api.post(`${HANDOVER_VIDEO}/complete`, { id, duration_s: durationS });

// Soft-delete a capture → crate returns to PENDING ("Video banaye").
export const discardHandoverVideo = (id) =>
  api.post(`${HANDOVER_VIDEO}/discard`, { id });

// Set/update the found-item qty for a crate, independent of the video (before
// or after recording). { crate_id, type, found_item_qty } → { id, found_item_qty }
export const setHandoverFoundQty = (crateId, type, foundItemQty) =>
  api.post(`${HANDOVER_VIDEO}/found-qty`, {
    crate_id: crateId,
    type,
    found_item_qty: foundItemQty,
  });

// { crate_ids: string[], reason }
export const bypassHandoverVideo = (data) =>
  api.post(`${HANDOVER_VIDEO}/bypass`, data);

// Direct-to-S3 presigned PUT (no backend hop, no auth headers — the URL is
// the credential). Content-Type must match what /init presigned.
// The app reports a failed client→S3 upload so the backend row goes FAILED
// with the client error instead of sitting INITIATED forever.
export const reportHandoverVideoFailure = (id, reason) =>
  api.post(`${HANDOVER_VIDEO}/report-failure`, { id, reason });

// Slow spoke uplinks: 13MB has been observed to take ~4.5 min, so the timeout
// must be generous — a stall aborts at this cap and the retry flow takes over.
const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

// blob-util wants file paths without the file:// scheme; content:// URIs pass
// through (resolved natively via ContentResolver).
const toUploadPath = (fileUri) =>
  fileUri?.startsWith("file://") ? fileUri.replace("file://", "") : fileUri;

// Best-effort check that a saved recording still exists on disk (Android may
// purge the camera cache). content:// URIs can't be stat'd — let upload try.
export const localFileExists = async (fileUri) => {
  if (!fileUri) return false;
  if (fileUri.startsWith("content://")) return true;
  try {
    return await RNFetchBlob.fs.exists(toUploadPath(fileUri));
  } catch {
    return false;
  }
};

// Streams the file from disk straight to the socket (native file-backed
// request body) — constant memory regardless of video size, with upload
// progress. No backend hop, no auth headers — the presigned URL is the
// credential; Content-Type must match what /init presigned.
export const uploadToPresignedUrl = async (
  uploadUrl,
  fileUri,
  contentType,
  onProgress,
) => {
  const task = RNFetchBlob.config({ timeout: UPLOAD_TIMEOUT_MS }).fetch(
    "PUT",
    uploadUrl,
    { "Content-Type": contentType },
    RNFetchBlob.wrap(toUploadPath(fileUri)),
  );
  if (onProgress) {
    task.uploadProgress((sent, total) => onProgress(sent, total));
  }
  const res = await task;
  const status = res.info().status;
  if (status < 200 || status >= 300) {
    let text = "";
    try {
      text = String(res.text()).slice(0, 300);
    } catch {
      // response body unreadable — status alone is enough
    }
    throw new Error(`presigned upload ${status}: ${text}`);
  }
  return { status };
};

export const updateProfileImage = (user_image) =>
  api.post("leader/update-profile-image", { user_image });

export const updateLeaderName = (data) =>
  api.post("/leader/edit-leader-name", data);

export const submitLeaderKyc = (data) => api.post("/leader/submit-kyc", data);

export const getLeaderKycInfo = () => api.get("/leader/get-kyc");

export const shouldShowKycNudge = () => api.get("/leader/show-kyc-nudge");

export const getAppBootParams = () => api.get("/leader/setup-4");
// export const getAppBootParams = () => api.get("/leader/setup-2");

export const toggleCMAddCustomer = (radius) =>
  api.post("/leader/set-serviceability-radius", {
    radius,
  });

export const viewCartLite = ({ userId }) =>
  api.get(`/leader/view-cart-light?userId=${userId}`);

export const deliveryTimeline = () => api.get("/leader/delivery-timeline");
export const getAllCohorts = () => api.get("/leader/customer-cohorts-list");

export const getCohortById = (id) => api.get(`/leader/customer-cohorts/${id}`);

export const getCartCMS = (userId) =>
  api.get(`/leader/view-cart-cms?userId=${userId}`);

export const useCashBack = (user_id) =>
  api.post("/leader/use-cashback", {
    user_id,
  });

export const dontUseCashBack = (user_id) =>
  api.post("/leader/dont-use-cashback", {
    user_id,
  });
// import jsonData from "./view-cart-cms.json";
// export const getCartCMS = () =>
//   new Promise((resolve, reject) => resolve(jsonData));

export const payNowMakeOnlinePayment = (payementData) =>
  api.post("/db/make-online-payment", payementData);

export const payNowCompleteOnlinePayment = (payementData) =>
  api.post("/db/confirm-online-payment", payementData);

export const requestPayNowOnlinePayment = () =>
  api.get("/db/request-online-payment");

// return_replace

export const getDelayedData = () => api.get("/leader/delayed-setup-4");
export const getReturnReplaceOrderDetails = (order_id = "") =>
  api.get(`/leader/return-replace-order-details/${order_id}`);

export const returnRequest = (body) =>
  api.post("/leader/request-return-order-item", body);

export const replaceRequest = (body) =>
  api.post("/leader/request-replace-order-item", body);

// pancard api
export const getPanCardCheck = () =>
  api.get("leader/check-commission-for-pancard");

export const submitPanData = (body) =>
  api.post("leader/add-pancard-details", body);

export const getLGL = () => api.get("/leader/lgl/my-journey");

export const getNudge = () => api.get("/leader/nudges");

export const saveDeviceLocation = (body) =>
  api.post("/leader/profile/device-coords", body);

export const getNotificationByLeaderId = (leaderId) =>
  api.get(`/leader/in-app-notifications/leader-id/${leaderId}`);

export const getCLLocation = () => api.get("/leader/address");
export const getAgentDetails = () => api.get("/leader/get-ae-details");

export const submitPoll = ({ pollId, answerId }) =>
  api.post(`/leader/submit-poll-response/${pollId}`, {
    answerId,
  });
export const submitSubjectiveQuestionPoll = ({ pollId, subjectiveAnswer }) =>
  api.post(`/leader/submit-poll-response/${pollId}`, {
    subjectiveAnswer,
  });

export const getPollFormItems = (pollFormId) =>
  api.get(`/leader/poll-form-items/${pollFormId}`);

export const submitPollFormResponses = (data) =>
  api.post("/leader/submit-poll-form-responses", data);

export const getStreamGroups = () => api.get("/getstream/get-groups");

export const getStreamToken = (data) => {
  return api.post("/getstream/getStreamToken", data);
};

export const createPost = (data) => {
  return api.post("/getstream/postCreated", data);
};

export const updatePost = (data) => {
  return api.post("/getstream/updatePost", { activity: JSON.stringify(data) });
};

export const retrieveUser = (data) => {
  return api.post("/getstream/retrieveUser", data);
};

export const getAllTags = () => {
  return api.get("/getstream/allTags");
};

export const getCmoCx = (lastKey = {}) => {
  return api.post("/leader/get-cmocx-near-cl-v3", {
    lastKey,
  });
};

export const getCmoCxSummary = () => {
  return api.get("/leader/cmocx-summary");
};

export const rejectCmoCx = (userId, reject_reason) => {
  return api.post("/leader/nahi-jodo", {
    user_id: userId,
    reject_reason,
  });
};

export const acceptCmoCx = (userId, userName) => {
  return api.post("/leader/cl-jodo", { user_id: userId, user_name: userName });
};
// order tracking

export const getOrderStates = (order_id) => {
  return api.get(`/leader/v4/order-states?order_id=${order_id}`);
};

export const reportRTO = (data) => api.post("/leader/v2/report-rto", data);
export const postActionCall = (data, url) => api.post(url, data);

export const markCxOrderDelivered = (data) =>
  api.post("/leader/mark-cx-order-as-delivered-3-single", data);

export const getPollData = (id) => {
  const url = `/leader/poll?pollId=${id}`;
  return api.get(url);
};

export const getAllPostData = (payload = {}, postIds = []) => {
  // TODO: Shouldn't perform any operations here
  if (postIds.length <= 200) {
    payload.post_ids = postIds.reduce(
      (acc, curr) => ({ ...acc, [curr.uuid]: true }),
      {},
    );

    payload.post_ids_count = Object.keys(payload.post_ids).length;
  } else payload.post_ids_count = 200; // static if post id is greater than 200
  return api.post("wall/v3/community/post-list", payload);
};

export const submitPost = (data) => {
  return api.post("wall/v2/community/post", data);
};

export const likePost = (id) => {
  return api.post(`wall/v1/community/post/${id}/like`);
};

export const unlikePost = (id) => {
  return api.delete(`wall/v1/community/post/${id}/like`);
};

export const commentPost = (id, data) => {
  return api.post(`wall/v1/community/post/${id}/comment`, data);
};
export const storeLeader = (id) => {
  return api.get(`/leader/v2/monthwise-sales-commission?team_leader_id=${id}`);
};
export const getUserPost = (id, timestamp) => {
  const url = timestamp
    ? `wall/v1/community/user/${id}/post-list?last_updated_at_timestamp=${timestamp}`
    : `wall/v1/community/user/${id}/post-list`;
  return api.get(url);
};

export const getWhatsappURL = (postId, userId) => {
  return `wall/v1/template/${userId}/post/${postId}/share`;
};

export const sharePost = (postId) => {
  return api.post(`wall/v1/community/post/${postId}/share`);
};

export const getNotifications = (timestamp) => {
  const url = timestamp
    ? `wall/v1/user/notification?created_at_timestamp=${timestamp}&type=wall-notification`
    : "wall/v1/user/notification";
  return api.get(url);
};

// Language
export const setLang = (data) => api.post("/leader/set-user-language", data);

export const getLang = () => api.get("/leader/get-languages");

export const fetchReturnOrders = ({
  offset = 0,
  limit = 10,
  query = "",
  date,
}) =>
  api.get(
    `/leader/rvp/get-returns?offset=${offset}&limit=${limit}&searchTerm=${query}&date=${date}`,
  );

export const fetchReturnOrdersV2 = ({
  offset = 0,
  limit = 10,
  query = "",
  date,
}) =>
  api.get(
    `/leader/rvp/get-returns-v2?offset=${offset}&limit=${limit}&searchTerm=${query}&date=${date}`,
  );

export const returnRequestCancel = ({ return_req_id }) =>
  api.post(`/leader/rvp/cancel/${return_req_id}`);

export const returnRequestCancelV2 = ({ return_req_id, type }) =>
  api.post("/leader/rvp/cancel-v2", { return_req_id, type });

export const scheduleRVPPickupDate = (data) =>
  api.post("/leader/rvp/schedule", data);

// return & redund flow related apis
export const getRvpOrderDetails = (order_id) => {
  return api.get(`leader/rvp/order-v2/${order_id}`);
};

export const submitRvpTicket = (data) => {
  return api.post("leader/rvp/submit", data);
};

export const submitRvpTicketMultiple = (data) => {
  return api.post("leader/rvp/submit/multiple", data);
};

export const fetchPostDataFromPostId = (post_id) => {
  return api.get(`wall/v1/community/post/${post_id}`);
};
export const setNotificationStatus = (id) => {
  return api.patch(`wall/v1/user/notification/${id}/visited`);
};
export const startWallSession = () => {
  return api.post("wall/v1/user/session/wall");
};

export const endWallSession = (session_id, end_time) => {
  const url = end_time
    ? `wall/v1/user/session/${session_id}/end?end_time=${end_time}`
    : `wall/v1/user/session/${session_id}/end`;
  return api.patch(url);
};

export const followUser = (id) => {
  return api.post(`wall/v1/community/user/${id}/follow`);
};

export const unfollowUser = (id) => {
  return api.delete(`wall/v1/community/user/${id}/follow`);
};

export const getUserFollowStat = (id) => {
  return api.get(`wall/v1/community/user/${id}/profile`);
};

export const getUserFollowerList = (userId, page = 1) => {
  return api.get(`wall/v1/community/user/${userId}/followers/${page}`);
};

export const getUserFollowingList = (userId, page = 1) => {
  return api.get(`wall/v1/community/user/${userId}/followings/${page}`);
};

export const trackPostVisitedImpression = (userId, postIds) => {
  return api.post(`/analytics/wall-impression/${userId}`, {
    type: "post_visited",
    data: postIds,
  });
};
// Sales Report

export const getSales = (selectedMonth) => {
  return api.get(`us/sales-report/get-overall-report?aggDate=${selectedMonth}`);
};
export const deactivateAccount = (payload) =>
  api.post("leader/deactivate-leader-state", payload);

export const checkReactivationReason = () =>
  api.get("leader/check-cl-reactivation-data");

export const setReactivationReason = (payload) => {
  api.post("leader/reactivate-cl-data", payload);
};
// Vacation Mode

export const addVacations = (data) =>
  api.post("/leader/cl-vacation-v2/add-vacation", data);
export const deleteVacation = (data) =>
  api.post("/leader/cl-vacation-v2/delete-vacation", data);
export const updateVacation = (data) =>
  api.post("/leader/cl-vacation-v2/update-vacation", data);
export const getVacationInfo = () =>
  api.get("/leader/cl-vacation-v2/get-vacation-info");
// My Shop

export const getShopStats = ({ id, date }) => {
  return api.get(`/us/my-shop/get-sales-overview?id=${id}&aggDate=${date}`);
};

export const getUpdates = ({ limit = 10, offset = 0 }) => {
  return api.get(
    `/us/my-shop/get-real-time-data-v2?limit=${limit}&offset=${offset}`,
  );
};

export const getLastMonthsOverview = (currMonth) => {
  return api.get(
    `us/sales-report/get-last-6-months-overview?aggDate=${currMonth}`,
  );
};

export const getApplinkData = (hash) => api.get(`/leader/v1/applinks/${hash}`);

export const getClTaskPrgressData = () =>
  api.get("/leader/cl-tasks/get-cl-todays-tasks");

export const getClTaskSummary = () =>
  api.get("/leader/cl-tasks/todays-task-summary");
export const getFeedbackReasonClTask = ({ subtype = "promotion" }) =>
  api.get(`/leader/cl-tasks/reasons/${subtype}`);

export const getBusinessTips = ({ subtype = "promotion" }) =>
  api.get(`/leader/cl-tasks/todays-tips-and-status/${subtype}`);

export const getCartTips = () =>
  api.get("/leader/cl-tasks/todays-tips-and-status/abandoned-cart");

export const getTaskData = ({ subtype }) =>
  api.get(`/leader/cl-tasks/todays-details/${subtype}`);

export const postTaskDone = ({ data, subtype, id }) =>
  api.post(`/leader/cl-tasks/mark-task-done/${subtype}/${id}`, data);

export const reportSupport = (data) => {
  return api.post("/support/v2/cl/report", data);
};

export const getDailyDeliveries = () => {
  return api.get("us/daily-deliveries/get-daily-deliveries");
};

export const getNextDeliveries = () => {
  return api.get("us/daily-deliveries/get-next-deliveries");
};

// My Deliveries
export const getTodaysCrateDeliveries = () => {
  return api.get(
    "/leader/daily-deliveries-v3/overview-v3?dataType=TODAY&noOfDays=1",
  );
};

export const getTodaysCrateDeliveriesV5 = () => {
  return api.get(
    "/leader/daily-deliveries-v3/overview-v5?dataType=TODAY&noOfDays=1",
  );
};

export const getPreviousCrateDeliveries = () => {
  return api.get("/leader/daily-deliveries-v3/group?dataType=PAST&noOfDays=7");
};

export const getUpcomingDeliveries = () => {
  return api.get(
    "/leader/daily-deliveries-v3/group?dataType=FUTURE&noOfDays=7",
  );
};

export const getCrateDeliveryDetails = ({
  date,
  spokeName = "",
  type = "NOT_ASSIGNED",
}) => {
  return api.get(
    `/leader/daily-deliveries-v3/daily-deliveries?type=${type}&date=${date}&spokeName=${spokeName}`,
  );
};

export const getDeliveryOrderItems = (data) => {
  return api.post(`/leader/daily-deliveries-v3/cp-get-cx-items-v3`, data);
};

export const itemsQuantityStatusV2 = (data) => {
  return api.post(`/leader/daily-deliveries-v3/v2/cp-pack-cx-item`, data);
};

export const itemsQuantityStatusV3 = (data) => {
  return api.post(`/leader/daily-deliveries-v3/cp-pack-cx-item-v3`, data);
};

export const markFoundStatus = ({ order_id, order_item_id, ofd_date }) => {
  return api.post(`/leader/daily-deliveries-v3/found-mark-item`, {
    order_id,
    order_item_id,
    ofd_date,
  });
};

export const itemsAddedInBag = (data) => {
  return api.post(`/leader/daily-deliveries-v3/cp-submit-cx`, data);
};

export const itemsQuantityStatus = (data) => {
  return api.post(`/leader/daily-deliveries-v3/cp-pack-cx-item`, data);
};

export const getComboItems = (data) => {
  return api.post(`/leader/daily-deliveries-v3/cp-get-cx-combo-items`, data);
};

export const getIssuesList = ({ date, spoke_name }) => {
  return api.get(
    `/leader/daily-deliveries-v3/get-issues-list?date=${date}&spoke_name=${spoke_name}`,
  );
};

export const submitComboItems = (data) => {
  return api.post(`/leader/daily-deliveries-v3/cp-pack-cx-combo-item`, data);
};

export const getDailyRTOSV3 = ({ date, spokeName = "" }) => {
  return api.get(
    `/leader/daily-deliveries-v3/daily-rtos?date=${date}&spokeName=${spokeName}`,
  );
};

export const getReturnDetailsV3 = ({ date, spokeName = "" }) => {
  return api.get(
    `/leader/daily-deliveries-v3/daily-returns?date=${date}&spokeName=${spokeName}`,
  );
};

export const getSkuOrdersDetails = (data) => {
  return api.post(`/leader/daily-deliveries-v3/get-sku-orders-details`, data);
};

export const getRecentLedgerTxns = (limit = 1000, latestId) => {
  let uri = `/leader/daily-deliveries-v3/recent-ledger-txns?limit=${limit}`;
  if (latestId) {
    uri += `&latestId=${latestId}`;
  }
  return api.get(uri);
};
export const getRecentLedgerTxnsv2 = ({ payload }) => {
  let uri = `/leader/daily-deliveries-v3/recent-ledger-txns-v2`;
  return api.post(uri, payload);
};

export const getLedgerItemModalData = ({ modal_api }) => {
  return api.get(modal_api);
};

// Cl Contest
export const contestDetails = ({ contest_id = 1 }) =>
  api.get(`cl-contest/leader/contest?contest_id=${contest_id}`);
export const pastContest = ({ limit = 10, offset = 0 }) =>
  api.get(`cl-contest/leader/past-contests?limit=${limit}&offset=${offset}`);
export const upcomingAndLive = ({ limit = 10, offset = 0 }) =>
  api.get(
    `cl-contest/leader/live-and-upcoming-contests?limit=${limit}&offset=${offset}`,
  );
export const myCustomers = ({ contestId = "", limit = 10, offset = 0 }) =>
  api.get(
    `cl-contest/leader/contest-cx?contestId=${contestId}&limit=${limit}&offset=${offset}`,
  );
export const contestCxOrdered = ({ contestId = "", limit = 10, offset = 0 }) =>
  api.get(
    `cl-contest/leader/contest-cx-ordered?contestId=${contestId}&limit=${limit}&offset=${offset}`,
  );

export const setContestResultViewed = (data) =>
  api.post("cl-contest/leader/contest-result-viewed", data);

export const contestSearchTerm = ({ contest_id = 1, searchTerm = "" }) =>
  api.get(
    `cl-contest/leader/contest-cx?contestId=${contest_id}&searchTerm=${searchTerm}`,
  );
export const getEarlyJourneyQuestionaire = () => {
  return api.get("/leader/early-journey-questions");
};

export const answerEarlyJourneyQuestionaire = (payload) => {
  return api.post("/leader/early-journey-questions/answer", payload);
};

export const earlyJourneyUpdateShopName = (payload) =>
  api.post("/leader/early-journey-update-shop-name", payload);
export const getRvpCXRTO = (orderId) => {
  return api.get(`leader/rvp/cx-rto-page/${orderId}`);
};

export const getCtareRvpCXRTO = (orderId) => {
  return api.get(`leader/rvp/crate-cx-rto-page/${orderId}`);
};

export const createCXRTO = ({ order_id = "", reason = "" }) =>
  api.post("leader/rvp/create-cx-rto", { order_id, reason });

export const crateCreateCXRTO = ({
  order_id = "",
  reason = "",
  lat = null,
  lng = null,
}) =>
  api.post("leader/rvp/crate-create-cx-rto", { order_id, reason, lat, lng });

// Deliveries v2
export const getDeliveriesOverview = () => {
  return api.get("/leader/mark-deliveries-v3/widget");
};
export const getDeliveryReturn = () => {
  return api.get("/leader/my-deliveries-v2/deliveries-pickups");
};
export const getRefunds = () => {
  return api.get("/leader/my-deliveries-v2/refunds");
};

export const rescheduleDelivery = (user_id, order_id, action_name) => {
  return api.post("/leader/my-deliveries-v2/reschedule-delivery", {
    user_id,
    order_id,
    action_name,
  });
};

export const markPickup = (return_req_id, otp) => {
  return api.post(`/leader/rvp/mark-picked-from-cx/${return_req_id}`, { otp });
};
export const cancelPickupReq = (return_req_id, otp, type = "rvp") =>
  api.post("/leader/rvp/cancel-v2", { otp, return_req_id, type });

export const unMarkPickup = (return_req_id) => {
  return api.post(`/leader/rvp/unmark-picked-from-cx/${return_req_id}`);
};

export const markDelivery = (order_id, otp, lat, lng) => {
  return api.post("/leader/mark-cx-order-as-delivered-3-single", {
    order_id,
    otp,
    lat,
    lng,
  });
};
export const rejectDelivery = (id, type = "cx_rto") => {
  return api.post("/leader/rvp/cancel-v2", { return_req_id: id, type });
};
export const unMarkDelivery = (order_id) => {
  return api.post("/leader/unmark-cx-order-as-delivered-2-single", {
    order_id,
  });
};

export const markRefunded = (return_req_id, otp) => {
  return api.post(`/leader/mark-refunded-to-cx/${return_req_id}`, { otp });
};
export const unMarkRefunded = (return_req_id) => {
  return api.post(`/leader/unmark-refunded-to-cx/${return_req_id}`);
};

export const markAction = (action) => {
  return api.post("/leader/mark-action", { action });
};

export const markMyDeliveyModalAction = (action) => {
  return api.post("/leader/mark-my-delivery-info-modal-action", { action });
};

export const gteBrownLogisticsConfig = () =>
  api.get("/leader/brown-logistics/config");
export const getAcceptedRequests = () =>
  api.get("/leader/brown-logistics/accepted-requests");
export const getPendingRequests = () =>
  api.get("/leader/brown-logistics/all-pending-requests");
export const acceptRejectBrownLogisticsOrder = ({ order_id = "", type = "" }) =>
  api.post(`/leader/brown-logistics/${type}`, { order_id });
export const trackAction = (event_name) =>
  api.post("/leader/brown-logistics/track-action", { event_name });

export const getInfoDeliveryFlow = () => api.get("/leader/delivery-flow/info");
export const confirmOrderDeliveryFlow = (data = {}) =>
  api.post("/leader/delivery-flow/confirm-order", data);

export const fetchPerformanceMetricsOverview = () =>
  api.get("/leader/order-metrics/overview");

export const fetchPerformanceMetricsData = (type) =>
  api.get(`/leader/order-metrics/detailed?type=${type}`);

export const fetchDeliveryRating = (month) =>
  api.get(`/leader/partner-performance/delivery-rating?month=${month}`);

export const fetchCustomerComplaints = () =>
  api.get(`/leader/partner-performance/customer-complaints`);

export const getDeliveryDetailsOverview = (payload) =>
  api.post("/leader/daily-deliveries-details-v3/get-delivery-details", payload);

export const getReturnDetailsOverview = (payload) =>
  api.post("/leader/daily-deliveries-details-v3/get-return-details", payload);

export const getOverviewItemDetails = ({ type, id }) =>
  api.get(
    `/leader/daily-deliveries-details-v3/get-item-details?type=${type}&id=${id}`,
  );

export const markCxFraud = (payload) =>
  api.post("/leader/mark-cx-fraud", payload);

export const getAllCpDbs = () => api.get("/leader/dbs-v2/all");

export const getLiveDbLocations = () => api.get("/leader/live-db-locations");

export const getLiveDbTrail = (db_id) =>
  api.get("/leader/live-db-locations/trail", { params: { db_id } });

export const addCpDb = (payload) => api.post("/leader/dbs-v2/add", payload);

export const deleteCpDb = ({ db_id, overide = false }) =>
  api.put(`/leader/dbs-v2/disable/${db_id}?override=${overide}`);

export const getCpDbRoutes = ({ db_id = "ALL" }) =>
  api.get(`/leader/dbs/routes?dbId=${db_id}`);

export const assignCpDbBulk = (payload) =>
  api.post("/leader/dbs/assign-db-bulk", payload);

export const unassignDb = (payload) =>
  api.post("/leader/dbs/unassign-db", payload);

export const switchDbDeliveries = (payload) =>
  api.post("/leader/dbs/switch-db-bulk", payload);

export const getDailyCratesItems = ({ type, date }) =>
  api.get(
    `/leader/daily-deliveries-v3/daily-crate-items?type=${type}&date=${date}`,
  );

export const getOrderUsersList = (payload) =>
  api.post("/leader/daily-deliveries-v3/users-list-v2", payload);

export const scanCrate = ({ crate_id, type, payload }) =>
  api.put(
    `/leader/daily-deliveries-v3/crate-scan-submit/${crate_id}?type=${type}`,
    payload,
  );

export const getRouteList = () => api.get("/leader/dbs-v2/routes-list");

export const addDBRoute = (payload) =>
  api.post("/leader/dbs-v2/create-route", payload);

export const deleteDBRoute = (payload) =>
  api.post("/leader/dbs-v2/delete-route", payload);

export const updateDBRoute = (payload) =>
  api.post("/leader/dbs-v2/update-route", payload);

export const getDbTouchpoints = ({ route_id = "ALL", type = "TODAY" }) =>
  api.get(`/leader/dbs-v2/touchpoints?type=${type}&route_id=${route_id}`);

export const assignTouchpointBulk = (payload) =>
  api.post("/leader/dbs-v2/assign-touchpoint-bulk", payload);

export const assignTouchpoint = (payload) =>
  api.post("/leader/dbs-v2/assign-touchpoint", payload);

export const addToBag = (payload) =>
  api.post("/leader/daily-deliveries-v3/add-to-bag", payload);

export const getDeliveryTasks = (payload) =>
  api.post("/leader/mark-deliveries-v3/delivery-tasks-v2", payload);

export const createSarvCallAPI = (payload) =>
  api.post("/leader/mark-deliveries-v3/click-to-call", payload);

export const getDeliveryTasksMap = (payload) =>
  api.post("/leader/mark-deliveries-v3/delivery-tasks-map-v2", payload);

export const getUserOrders = ({
  user_id,
  data_type = "TODAY",
  db_id = "NOT_ASSIGNED",
  route_id = "NOT_ASSIGNED",
  trip_id = null,
}) => {
  // Multi-trip drill-in, same contract as partner-db's getUserOrders. trip_id
  // is APPENDED only when set, so omitting it builds the pre-trip URL. Values
  // the backend accepts (citymall-services #29232, isTripIdMatched):
  //   <uuid>    -> only that trip's orders
  //   PENDING   -> only orders that have never been given a trip
  //   (omitted) -> every order of the customer, unscoped
  const base = `/leader/mark-deliveries-v3/delivery-tasks-by-cx-id-v2/${user_id}?data_type=${data_type}&db_id=${db_id}&route_id=${route_id}`;
  return api.get(trip_id ? `${base}&trip_id=${trip_id}` : base);
};

export const markTaskActions = (payload) =>
  api.post(`/leader/mark-deliveries-v3/mark-task-actions-v2`, payload);

export const resendRescheduleOtp = (payload) =>
  api.post(`/leader/mark-deliveries-v3/resend-reschedule-otp`, payload);

export const markMultipleDeliveries = (payload) =>
  api.post("/leader/mark-deliveries-v3/cp-cx-complete-delivery", payload);

export const cancelRto = (payload) =>
  api.post("/leader/rvp/cancel-v2-multiple", payload);

// homepage widgets
export const getHomePageWidgets = () => api.get("/leader/homepage/widgets");
export const getHomePageWidgetData = () =>
  api.get("/leader/homepage/widgets-data");

// db management
export const getDBManageOverview = () => api.get("/leader/db-manage/overview");
export const getOutForDeliveryDb = () => api.get("/leader/db-manage/ofd-dbs");
export const getOutForDeliveryDbDetails = ({
  db_id,
  route_id = "ALL",
  search_term = "",
}) => {
  return api.get(
    `leader/db-manage/ofd-db-by-id/${db_id}?route_id=${route_id}&search_term=${search_term}`,
  );
};
export const markOutForDelivery = ({ payload, db_id }) =>
  api.post(`/leader/db-manage/ofd-mark-bulk-cx/${db_id}`, payload);

export const getRoutingList = () =>
  api.get("/leader/db-manage/routing-details");

// recon
export const getReconDbs = () => api.get("leader/db-manage/recon-dbs");
export const getReconByDbId = ({ db_id, search_term = "" }) =>
  api.get(
    `/leader/db-manage/recon-db-by-id/${db_id}?search_term=${search_term}`,
  );

export const submitReconCollectedCash = ({ payload, db_id }) =>
  api.post(`/leader/db-manage/recon-submit-collected-cash/${db_id}`, payload);

export const getReconHistory = () => api.get("/leader/db-manage/recon-history");

export const getReconHistoryByDbId = ({ db_id, ofd_date, search_term }) =>
  api.get(
    `/leader/db-manage/recon-history-by-id/${db_id}/${ofd_date}?search_term=${search_term}`,
  );

export const createOnlinePayment = (payload) =>
  api.post("/leader/create-payment", payload);
export const completeOnlinePayment = (payload) =>
  api.post("/leader/complete-payment", payload);
export const createOnlinePaymentV2 = (payload) =>
  api.post("/leader/create-payment-v2", payload);
export const completeOnlinePaymentV2 = (payload) =>
  api.post("/leader/complete-payment-v2", payload);
export const markItemLostDamaged = (payload) =>
  api.post("/leader/daily-deliveries-v3/cp-mark-item-lost-damage", payload);

export const markReconSubmit = ({ db_id, payload }) =>
  api.post(`/leader/db-manage/recon-mark-as-submitted/${db_id}`, payload);

export const getDeliveryPaymentTxns = ({ spoke_name }) =>
  api.get(
    `/leader/daily-deliveries-v3/delivery-payment-txns?spoke_name=${spoke_name}`,
  );

export const getDailyPerformance = (date) =>
  api.get(`/leader/v2/cp-performance/daily-performance?date=${date}`);

export const getWeeklyPerformance = (fromDate, toDate) =>
  api.get(
    `/leader/v2/cp-performance/weekly-performance?fromDate=${fromDate}&toDate=${toDate}`,
  );
export const getWeekDayPerformance = (date, fromDate, toDate) =>
  api.get(
    `/leader/v2/cp-performance/weekday-performance?date=${date}&fromDate=${fromDate}&toDate=${toDate}`,
  );

export const markTouchpointPickedUpByCpdb = (payload) =>
  api.post(`/leader/mark-deliveries-v3/pick-cx-items`, payload);

/* wallet v2 */
export const getWalletSummaryV2 = ({
  fromDate = "",
  toDate = "",
  type = "",
  TIME = "DEFAULT",
}) =>
  api.get(
    `/leader/v2/wallet/summary?type=${type}&TIME=${TIME}&start_date=${fromDate}&end_date=${toDate}`,
  );

export const getWalletMetaDataV2 = () => api.get(`/leader/v2/wallet/metadata`);

export const getWalletTransactionsV2 = ({
  fromDate = "",
  toDate = "",
  type = "",
  TIME = "DEFAULT",
  TRANSACTION = "TXN_DEFAULT",
  limit = 20,
  offset = 0,
}) =>
  api.get(
    `/leader/v2/wallet/transactions?type=${type}&TIME=${TIME}&TRANSACTION=${TRANSACTION}&start_date=${fromDate}&end_date=${toDate}&limit=${limit}&offset=${offset}`,
  );
export const raiseDispute = (data) => api.post(`/leader/raise-dispute`, data);

export const getTransactionDispute = (txn_id) =>
  api.get(`/leader/fetch-dispute?txn_id=${txn_id}`);

export const cancelDispute = (data) =>
  api.post(`/leader/cancel-dispute?txn_id=${data.txn_id}`);

export const getTodaysReturns = ({ date, spokeName = "" }) => {
  return api.get(
    `/leader/daily-deliveries-v3/sku-wise-returns-v2?date=${date}&spokeName=${spokeName}`,
  );
};

export const getBagsItemsData = ({
  order_ids,
  user_id,
  plastic_bag_opted_in,
}) =>
  api.post(`/leader/mark-deliveries-v3/get-items-list`, {
    order_ids,
    user_id,
    plastic_bag_opted_in,
  });

export const getDeliveryOtpViaSms = (
  phone_number,
  order_id,
  payable_amount,
) => {
  return api.post(`/leader/get-delivery-otp-via-sms`, {
    phone_number,
    order_id,
    payable_amount,
  });
};

export const createQrCodePayment = (
  order_ids = [],
  cx_id = "",
  payable_amount = 0,
) => {
  return api.post(`/leader/create-qr-payment`, {
    order_ids,
    cx_id,
    payable_amount,
  });
};
export const getQrCodeStatus = ({
  qrOrderId = "",
  otp_enabled = false,
  cx_id = "",
  order_ids = {},
}) => {
  return api.post(
    `/leader/get-qr-status?qr_order_id=${qrOrderId}&otp_enabled=${otp_enabled}&cx_id=${cx_id}`,
    { order_ids },
  );
};

export const getReturnDetail = (payload) =>
  api.post(`/leader/rvp/dashboard-details`, payload);

export const getPlanModalData = (cta_api = "") => {
  return api.get(cta_api);
};

//websocket apis

export const initializeWebSocketSession = async () => {
  const userToken = await getTokenZ();
  return {
    service: "partner_ws",
    route: "start_session",
    auth_token: userToken,
    payload: "start session",
  };
};

export const testRefreshButton = async () => {
  const userToken = await getTokenZ();
  return {
    service: "partner_ws",
    route: "button_refresh",
    auth_token: userToken,
    payload: "refresh button clicked",
  };
};
// type = CREDIT, DEBIT
export const getTransactionDetails = (id, type) =>
  api.get(
    `/leader/daily-deliveries-v3/credit-debit-and-crate-details?id=${id}&type=${type}`,
  );

// ── Partner Routing & Packing — DC Routing Packing Namespace ────────────────
// All Partner Routing & Packing screens hit /api/leader/dc-routing-packing/v1/*,
// backed by citymall-services routes/cl-db-manage-v2/dc-routing-packing.js.
// Server gate: req.teamLeader.is_cmo_cp === true → else 403.
// CpDeliveryRoutes (legacy screen) keeps using the older /leader/dbs-v2/*
// helpers defined earlier in this file.

const DC_ROUTING_PACKING = "/leader/dc-routing-packing/v1";

// Bulk permissions check — returns
// { is_cmo_cp, pages: { cp_routing, crate_cx_items } }. Replaces two
// /get-datum-for-page calls. Used by usePermission.
export const getDcRoutingPackingPermissions = () =>
  api.get(`${DC_ROUTING_PACKING}/permissions`);

// Big read of every DB / locality / crate / customer for the day. Same
// keys dc-routing-packing already reads (cp_dbs, tabs, data.{DC,CRATE,TP})
// plus the dc-routing-packing-only enrichment (route_id+route_name on
// tp_list[*] and CRATE, routes[] aggregated array on each cp_db).
export const getAssignedDbs = (type = "TODAY") =>
  api.get(`${DC_ROUTING_PACKING}/dbs-snapshot?type=${type}`);

// Assign / unassign / misroute customers — body unchanged from web's
// /assign-dbs: { tps:[{cp_id,cx_id,cp_db_id,route_id}], cp_db_id, route_id }
export const assignDbs = (body, type = "TODAY") =>
  api.post(`${DC_ROUTING_PACKING}/dbs/assign?type=${type}`, body);

// Crate search dropdown.
export const searchCrateV2 = ({ searchTerm = "", tab = "today" }) =>
  api.get(
    `${DC_ROUTING_PACKING}/crate/search?searchTerm=${encodeURIComponent(
      searchTerm,
    )}&tab=${tab}`,
  );

// Per-crate cx items page.
export const getCrateCxItems = ({ crate_id, tab = "today" }) =>
  api.get(
    `${DC_ROUTING_PACKING}/crate/items?crate_id=${encodeURIComponent(
      crate_id,
    )}&tab=${tab}`,
  );

// Pack-visible / unpack-visible — crate_id moves to the URL path; rest of
// the body unchanged (type, order_item_ids, is_rescheduled,
// reschedule_crate_packing_id).
export const packVisibleItems = ({ crate_id, ...body } = {}) =>
  api.post(
    `${DC_ROUTING_PACKING}/crate/${encodeURIComponent(crate_id)}/pack-visible`,
    body,
  );

export const unpackVisibleItems = ({ crate_id, ...body } = {}) =>
  api.post(
    `${DC_ROUTING_PACKING}/crate/${encodeURIComponent(crate_id)}/unpack-visible`,
    body,
  );

// Locality (= route) catalog with counts —
// [{ route_id, route_name, locality, user_count, crate_count }].
export const getSpokeRoutesCatalog = () =>
  api.get(`${DC_ROUTING_PACKING}/localities`);

// Bind / rename / unbind a route to a DB (dc-routing-packing-side flow). Distinct from
// the older addDBRoute/updateDBRoute/deleteDBRoute helpers above which the
// legacy CpDeliveryRoutes screen uses against /leader/dbs-v2/*.
export const addDBRouteDcRoutingPacking = ({ db_id, route_id, route_name }) =>
  api.post(`${DC_ROUTING_PACKING}/dbs/${encodeURIComponent(db_id)}/routes`, {
    route_id,
    route_name,
  });

export const updateDBRouteDcRoutingPacking = ({
  db_id,
  route_id,
  route_name,
}) =>
  api.put(
    `${DC_ROUTING_PACKING}/dbs/${encodeURIComponent(db_id)}/routes/${encodeURIComponent(route_id)}`,
    { route_name },
  );

export const deleteDBRouteDcRoutingPacking = ({ db_id, route_id }) =>
  api.delete(
    `${DC_ROUTING_PACKING}/dbs/${encodeURIComponent(db_id)}/routes/${encodeURIComponent(route_id)}`,
  );

// Marking — same payload contract as itemsQuantityStatusV3 above (which
// the CustomerOrderItems / MyDeliveries screens still use). The new
// helper puts order_item_id on the URL path and drops it from the body
// (server tolerates either, but path-param is cleaner).
export const markItemDcRoutingPacking = ({ order_item_id, ...body }) =>
  api.post(
    `${DC_ROUTING_PACKING}/items/${encodeURIComponent(order_item_id)}/marking`,
    body,
  );

// Add/Edit Bags — cx_id moves to the URL path, rest of payload unchanged.
export const addToBagDcRoutingPacking = ({ cx_id, ...body }) =>
  api.post(
    `${DC_ROUTING_PACKING}/customers/${encodeURIComponent(cx_id)}/bags`,
    body,
  );

// Inventory (TOP_10_SKUS) — aggregate-by-sku view of spoke-stashed items
// for a given (db_id, route_id) pair. Returns { skus: [{sku_id, sku_name,
// total_qty, packed_qty, unpacked_qty, cx_count, order_item_ids,
// unpacked_order_item_ids, per_cx: [{cx_id, cx_name, total_qty,
// packed_qty, unpacked_qty, order_item_ids, unpacked_order_item_ids}]}] }.
// per_cx supports the customer-grouped mobile view; flat fields stay for
// back-compat with the SKU-grouped view.
export const getDbInventoryDcRoutingPacking = ({ db_id, route_id }) =>
  api.get(
    `${DC_ROUTING_PACKING}/dbs/${encodeURIComponent(db_id)}/inventory?route_id=${encodeURIComponent(route_id)}`,
  );

// Pack-visible for inventory items — body carries {route_id,
// order_item_ids[]}. Server defense-in-depth re-validates every id
// against the same inventory query, then routes through
// packVisibleService.packVisibleItems with type='INVENTORY'.
export const packInventoryVisibleDcRoutingPacking = ({
  db_id,
  route_id,
  order_item_ids,
}) =>
  api.post(
    `${DC_ROUTING_PACKING}/dbs/${encodeURIComponent(db_id)}/inventory/pack-visible`,
    { route_id, order_item_ids },
  );

// Inventory aggregate marking — body carries {route_id, sku_id, diff,
// order_item_ids?}. Server distributes the diff across the SKU's
// unpacked order_item_ids (intersected with order_item_ids when
// provided, so the caller can scope marking to one customer's items) and
// fans out to postCpMarkCxItemV4/NonOfd kernel per item (one mark per
// item, one reason per call). Returns per-item success/error breakdown
// for partial-failure handling.
export const markInventoryDcRoutingPacking = ({
  db_id,
  route_id,
  sku_id,
  diff,
  order_item_ids,
}) =>
  api.post(
    `${DC_ROUTING_PACKING}/dbs/${encodeURIComponent(db_id)}/inventory/mark`,
    {
      route_id,
      sku_id,
      diff,
      ...(Array.isArray(order_item_ids) && order_item_ids.length > 0
        ? { order_item_ids }
        : {}),
    },
  );

// Spoke staff attendance. 4xx come back as a resolved response on purpose: the
// shared error interceptor would otherwise replace 404/409 with a generic
// Hindi snackbar (resolving undefined) and sign the user out on 401, but the
// screens show the server's own `message` inline.
const ATTENDANCE_REQUEST_CONFIG = { validateStatus: (status) => status < 500 };

export const getAttendanceRoles = () =>
  api.get("/leader/attendance/roles", ATTENDANCE_REQUEST_CONFIG);

export const identifyAttendanceSubject = ({ role, phone }) =>
  api.post(
    "/leader/attendance/identify",
    { role, phone },
    ATTENDANCE_REQUEST_CONFIG,
  );

export const getAttendanceHistory = ({ subjectToken, month }) =>
  api.get("/leader/attendance/history", {
    ...ATTENDANCE_REQUEST_CONFIG,
    params: { subject_token: subjectToken, month },
  });
